/**
 * Saves a pool's QR code as a picture (ISSUE-1339), the web half of v1's
 * "Share QR code". On a phone browser that can share files the picture goes
 * to the OS share sheet, so it can land in Messages or Photos. Everywhere
 * else it downloads as a PNG.
 *
 * Every browser global comes in through `env`, so the branches can be
 * asserted without a real canvas.
 */
import { palette } from "@sportsmet/theme";
import { buildQrPath } from "@sportsmet/ui";

/** Pixels per QR module in the saved picture. Big enough to print. */
const PNG_MODULE_PIXELS = 16;

export type typeQrSaveOutcome = "shared" | "downloaded" | "failed";

export type typeQrImageEnv = {
  createCanvas: () => HTMLCanvasElement;
  navigator: Pick<Navigator, "share" | "canShare"> | undefined;
  download: (blob: Blob, fileName: string) => void;
};

function defaultDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

const browserEnv = (): typeQrImageEnv => ({
  createCanvas: () => document.createElement("canvas"),
  navigator: typeof navigator === "undefined" ? undefined : navigator,
  download: defaultDownload,
});

/** A file name a fan will recognize in their downloads folder. */
export function qrFileName(poolName: string): string {
  const slug = poolName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${slug.length > 0 ? slug : "pool"}-qr-code.png`;
}

async function drawQrPng(
  value: string,
  createCanvas: () => HTMLCanvasElement,
): Promise<Blob | null> {
  const qr = buildQrPath(value);
  const canvas = createCanvas();
  canvas.width = qr.size * PNG_MODULE_PIXELS;
  canvas.height = qr.size * PNG_MODULE_PIXELS;
  const context = canvas.getContext("2d");
  if (context === null) return null;
  context.fillStyle = palette.neutral[0];
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.scale(PNG_MODULE_PIXELS, PNG_MODULE_PIXELS);
  context.fillStyle = palette.neutral[1000];
  context.fill(new Path2D(qr.path));
  return new Promise((resolve) => {
    canvas.toBlob(resolve, "image/png");
  });
}

export async function saveQrImage(
  input: { value: string; poolName: string },
  env: typeQrImageEnv = browserEnv(),
): Promise<typeQrSaveOutcome> {
  let blob: Blob | null;
  try {
    blob = await drawQrPng(input.value, env.createCanvas);
  } catch {
    return "failed";
  }
  if (blob === null) return "failed";
  const fileName = qrFileName(input.poolName);
  const file = new File([blob], fileName, { type: "image/png" });
  const nav = env.navigator;
  if (
    typeof nav?.share === "function" &&
    typeof nav.canShare === "function" &&
    nav.canShare({ files: [file] })
  ) {
    try {
      await nav.share({ files: [file], title: `Join ${input.poolName}` });
      return "shared";
    } catch {
      // Dismissing the share sheet rejects. The fan chose not to share; fall
      // through to nothing rather than forcing a download on them.
      return "shared";
    }
  }
  env.download(blob, fileName);
  return "downloaded";
}
